import { useState, useRef, useEffect } from 'react'
import { Button } from '../ui/Button'
import { 
  Settings, 
  Grid, 
  List, 
  ChevronDown,
  Monitor,
  Palette,
  Printer,
  Calculator
} from 'lucide-react'

interface POSSettingsDropdownProps {
  posLayout: 'cards' | 'table'
  setPosLayout: (layout: 'cards' | 'table') => void
  onGeneralSettings?: () => void
  onDisplaySettings?: () => void
  onPrinterSettings?: () => void
  onTaxSettings?: () => void
}

export const POSSettingsDropdown = ({
  posLayout,
  setPosLayout,
  onGeneralSettings,
  onDisplaySettings,
  onPrinterSettings,
  onTaxSettings
}: POSSettingsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 h-8 text-xs"
      >
        <Settings className="h-4 w-4" />
        Settings
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-10 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
          <div className="py-2">
            {/* Layout Section */}
            <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Layout
              </p>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    setPosLayout('cards')
                    setIsOpen(false)
                  }}
                  className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors ${
                    posLayout === 'cards'
                      ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Grid className="h-4 w-4" />
                  Browse and Add
                  {posLayout === 'cards' && (
                    <div className="ml-auto w-2 h-2 bg-orange-500 rounded-full"></div>
                  )}
                </button>
                <button
                  onClick={() => {
                    setPosLayout('table')
                    setIsOpen(false)
                  }}
                  className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors ${
                    posLayout === 'table'
                      ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <List className="h-4 w-4" />
                  Search and Add
                  {posLayout === 'table' && (
                    <div className="ml-auto w-2 h-2 bg-orange-500 rounded-full"></div>
                  )}
                </button>
              </div>
            </div>

            {/* Settings Section */}
            <div className="px-3 py-2">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Configuration
              </p>
              <div className="space-y-1">
                {onDisplaySettings && (
                  <button
                    onClick={() => {
                      onDisplaySettings()
                      setIsOpen(false)
                    }}
                    className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <Monitor className="h-4 w-4" />
                    Display Settings
                  </button>
                )}
                
                {onPrinterSettings && (
                  <button
                    onClick={() => {
                      onPrinterSettings()
                      setIsOpen(false)
                    }}
                    className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <Printer className="h-4 w-4" />
                    Printer Settings
                  </button>
                )}
                
                {onTaxSettings && (
                  <button
                    onClick={() => {
                      onTaxSettings()
                      setIsOpen(false)
                    }}
                    className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <Calculator className="h-4 w-4" />
                    Tax Settings
                  </button>
                )}
                
                {onGeneralSettings && (
                  <button
                    onClick={() => {
                      onGeneralSettings()
                      setIsOpen(false)
                    }}
                    className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    General Settings
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}